/**
 * EarnCreditsDialog — referral dashboard + ways to earn free credits.
 * Accessed from "Earn Credits" in the AccountButton dropdown.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getReferralInfo, claimReferral, recordSocialShare } from '@/api/rewardApi.ts';
import type { ReferralInfo } from '@/api/rewardApi.ts';
import { capabilities } from '@/capabilities.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import { useAccountStore } from '@/store/accountStore.ts';
import { ShareDialog } from './ShareDialog.tsx';
import { env } from '@/config/environment.ts';
import styles from './EarnCreditsDialog.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function EarnCreditsDialog({ open, onClose }: Props) {
  const { t } = useTranslation('account');
  const [info, setInfo] = useState<ReferralInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Claim referral state
  const [claimCode, setClaimCode] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState<string | null>(null);
  const [claimError, setClaimError] = useState('');

  // Social share state
  const [sharing, setSharing] = useState(false);
  const [shareStatus, setShareStatus] = useState<string | null>(null);
  const [shareIsError, setShareIsError] = useState(false);

  // Reviewer link share state
  const [shareOpen, setShareOpen] = useState(false);
  const [subscriptionGateOpen, setSubscriptionGateOpen] = useState(false);
  const isSubscribed = useAccountStore(s => s.isSubscribed);
  const project = useProjectStore(s => s.project);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError('');
    getReferralInfo()
      .then(setInfo)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [open]);

  const copyText = useCallback(async (text: string) => {
    if (capabilities.bridge) {
      const { enqueueSendToNative } = await import('@/bridge/embeddedBridge.ts');
      enqueueSendToNative('copyToClipboard', { text });
    } else {
      await navigator.clipboard.writeText(text);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  const handleCopyCode = useCallback(async () => {
    if (!info?.referral_code) return;
    try { await copyText(info.referral_code); } catch { /* fallback */ }
  }, [info?.referral_code, copyText]);

  const handleCopyLink = useCallback(async () => {
    if (!info?.referral_code) return;
    const link = `https://${env.shareDomain}?ref=${info.referral_code}`;
    try { await copyText(link); } catch { /* fallback */ }
  }, [info?.referral_code, copyText]);

  const handleClaimReferral = useCallback(async () => {
    if (!claimCode.trim()) return;
    setClaiming(true);
    setClaimError('');
    setClaimResult(null);
    try {
      const result = await claimReferral(claimCode);
      if (result.rewarded) {
        setClaimResult(t('claimSuccess', { credits: result.credits_awarded }));
        setClaimCode('');
        // Refresh info to update stats
        const updated = await getReferralInfo();
        setInfo(updated);
      } else {
        const reasons: Record<string, string> = {
          already_referred: t('errorAlreadyReferred'),
          referrer_cap_reached: t('errorReferrerCapReached'),
        };
        setClaimError(reasons[result.reason ?? ''] ?? t('errorCouldNotClaim'));
      }
    } catch (err) {
      setClaimError(err instanceof Error ? err.message : t('errorFailedToClaimReferral'));
    } finally {
      setClaiming(false);
    }
  }, [claimCode, t]);

  const handleShareNow = useCallback(async () => {
    setSharing(true);
    setShareStatus(null);
    setShareIsError(false);
    try {
      const { requestExportForShare } = await import('@/bridge/embeddedBridge.ts');
      const activeSceneId = useProjectStore.getState().activeSceneId;
      const scope = activeSceneId != null ? 'currentScene' : 'allScenes';
      const result = await requestExportForShare(scope);

      if (result.completed) {
        const reward = await recordSocialShare();
        if (reward.rewarded) {
          useAccountStore.getState().refreshBalance();
          setShareStatus(t('shareStatusSuccess'));
        } else if (reward.reason === 'cooldown') {
          setShareStatus(t('shareStatusCooldown'));
        }
      }
    } catch (err) {
      console.warn('[EarnCreditsDialog] Share failed:', err);
      setShareIsError(true);
      setShareStatus(t('shareStatusFailed'));
    } finally {
      setSharing(false);
    }
  }, [t]);

  if (!open) return null;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <span className={styles.title}>{t('earnCreditsTitle')}</span>
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>

        <div className={styles.body}>
          {loading ? (
            <div className={styles.loading}>{t('earnCreditsLoading')}</div>
          ) : error ? (
            <div className={styles.error}>{error}</div>
          ) : info ? (
            <>
              {/* Claim a referral code — shown first if user hasn't been referred yet */}
              {!info.has_been_referred && (
                <div className={styles.claimSection}>
                  <div className={styles.sectionTitle}>{t('haveReferralCode')}</div>
                  <div className={styles.claimRow}>
                    <input
                      className={styles.claimInput}
                      type="text"
                      maxLength={8}
                      placeholder={t('enterCodePlaceholder')}
                      value={claimCode}
                      onChange={e => setClaimCode(e.target.value.toUpperCase())}
                      onKeyDown={e => { if (e.key === 'Enter') handleClaimReferral(); }}
                    />
                    <button
                      className={styles.claimBtn}
                      onClick={handleClaimReferral}
                      disabled={claiming || claimCode.trim().length < 8}
                    >
                      {claiming ? t('claimingButton') : t('claimButton')}
                    </button>
                  </div>
                  {claimError && <div className={styles.error}>{claimError}</div>}
                  {claimResult && <div className={styles.success}>{claimResult}</div>}
                </div>
              )}

              {/* Referral code section */}
              <div className={styles.section}>
                <div className={styles.sectionTitle}>{t('yourReferralCode')}</div>
                <div className={styles.sectionDesc}>
                  {t('referralDescription', { cap: info.monthly_cap })}
                </div>
                <div className={styles.codeRow}>
                  <div className={styles.codeDisplay}>{info.referral_code}</div>
                  <button
                    className={copied ? styles.copyBtnSuccess : styles.copyBtn}
                    onClick={handleCopyCode}
                  >
                    {copied ? t('copiedButton') : t('copyButton')}
                  </button>
                </div>
                <button className={styles.copyBtn} onClick={handleCopyLink} style={{ width: '100%', marginTop: 4 }}>
                  {t('copyReferralLink')}
                </button>
              </div>

              {/* Stats */}
              <div className={styles.statsRow}>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>{info.total_referrals}</div>
                  <div className={styles.statLabel}>{t('statTotalReferrals')}</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>{info.monthly_referrals}/{info.monthly_cap}</div>
                  <div className={styles.statLabel}>{t('statThisMonth')}</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>{info.total_credits_earned}</div>
                  <div className={styles.statLabel}>{t('statCreditsEarned')}</div>
                </div>
              </div>

              <div className={styles.divider} />

              {/* Ways to earn */}
              <div className={styles.section}>
                <div className={styles.sectionTitle}>{t('waysToEarnTitle')}</div>
                <div className={styles.rewardList}>
                  <div className={styles.rewardItem}>
                    <span className={styles.rewardName}>{t('rewardReferFriend')}</span>
                    <span className={styles.rewardAmount}>{t('rewardReferAmount')}</span>
                  </div>
                  <div className={styles.rewardItem}>
                    <span className={styles.rewardName}>{t('rewardShareSocial')}</span>
                    <span className={styles.rewardAmount}>{t('rewardShareAmount')}</span>
                    {capabilities.bridge && (
                      <button
                        className={styles.claimBtn}
                        onClick={handleShareNow}
                        disabled={sharing}
                        style={{ marginLeft: 8 }}
                      >
                        {sharing ? t('sharingButton') : t('shareButton')}
                      </button>
                    )}
                  </div>
                  {shareStatus && (
                    <div className={shareIsError ? styles.error : styles.success}>
                      {shareStatus}
                    </div>
                  )}
                  <div className={styles.rewardItem}>
                    <span className={styles.rewardName}>{t('rewardReviewerLink')}</span>
                    <span className={styles.rewardAmount}>{t('rewardReviewerAmount')}</span>
                    <button
                      className={styles.claimBtn}
                      onClick={() => {
                        if (capabilities.bridge && !isSubscribed) {
                          setSubscriptionGateOpen(true);
                        } else {
                          setShareOpen(true);
                        }
                      }}
                      disabled={!project}
                      style={{ marginLeft: 8 }}
                    >
                      {t('shareButton')}
                    </button>
                  </div>
                </div>
              </div>

            </>
          ) : null}
        </div>
      </div>

      {/* Share for review dialog (same as main timeline Share button) */}
      {shareOpen && <ShareDialog open onClose={() => setShareOpen(false)} />}

      {/* Subscription gate dialog */}
      {subscriptionGateOpen && (
        <div className={styles.backdrop} onClick={() => setSubscriptionGateOpen(false)}>
          <div className={styles.dialog} onClick={e => e.stopPropagation()} style={{ maxWidth: 340 }}>
            <div className={styles.header}>
              <span className={styles.title}>{t('subscriptionRequiredTitle')}</span>
              <button className={styles.closeBtn} onClick={() => setSubscriptionGateOpen(false)}>&times;</button>
            </div>
            <div className={styles.body}>
              <p style={{ margin: '0 0 16px', color: 'var(--text-secondary, #aaa)' }}>
                {t('subscriptionRequiredMessage')}
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className={styles.copyBtn} onClick={() => setSubscriptionGateOpen(false)}>{t('cancelButton')}</button>
                <button className={styles.claimBtn} onClick={() => {
                  setSubscriptionGateOpen(false);
                  import('@/bridge/embeddedBridge.ts').then(({ enqueueSendToNative }) => {
                    enqueueSendToNative('showPurchasePanel', {});
                  });
                }}>{t('continueButton')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
